import React, { useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import GlowScoreBadge from '../common/GlowScoreBadge';
import TagPill from '../common/TagPill';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiDroplet, FiSun, FiMoon, FiZap, FiRefreshCw, FiAlertCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

function PhotoGallery({ entry, onClose }) {
  // Normalise: prefer photos array, fall back to imageUrl
  const photos = entry.photos?.length
    ? entry.photos
    : entry.imageUrl
    ? [{ id: 'legacy', url: entry.imageUrl, useInTimelapse: true }]
    : [];

  const [idx, setIdx] = useState(0);
  const current = photos[idx];

  return (
    <div className="relative h-80 w-full overflow-hidden flex-shrink-0 bg-stone-900">
      {/* Images */}
      {photos.length > 0 ? (
        photos.map((p, i) => (
          <img
            key={p.id}
            src={p.url}
            alt={`skin ${i + 1}`}
            className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-300"
            style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? 'auto' : 'none' }}
          />
        ))
      ) : (
        <div className="h-full flex items-center justify-center text-white/20 text-6xl">✦</div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent pointer-events-none" />

      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-6 right-5 w-10 h-10 rounded-full bg-white/80 backdrop-blur-md flex items-center justify-center shadow-sm z-10"
      >
        <SafeIcon icon={FiX} className="text-slate-800 text-lg" />
      </button>

      {/* Prev/next arrows */}
      {photos.length > 1 && (
        <>
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white disabled:opacity-0 transition-opacity z-10"
          >
            <SafeIcon icon={FiChevronLeft} className="text-lg" />
          </button>
          <button
            onClick={() => setIdx(i => Math.min(photos.length - 1, i + 1))}
            disabled={idx === photos.length - 1}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white disabled:opacity-0 transition-opacity z-10"
          >
            <SafeIcon icon={FiChevronRight} className="text-lg" />
          </button>
        </>
      )}

      {/* Date / glow */}
      <div className="absolute bottom-6 left-5 right-5 flex justify-between items-end z-10">
        <div>
          <p className="text-white font-sans text-4xl font-medium tracking-tight drop-shadow-md">
            {format(parseISO(entry.date), 'MMMM d')}
          </p>
          <p className="text-white/90 font-sans text-sm mt-1 drop-shadow-md">
            {format(parseISO(entry.date), 'EEEE, yyyy')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <GlowScoreBadge score={entry.glowScore} size="lg" />
          {photos.length > 1 && (
            <div className="flex gap-1.5">
              {photos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? 'bg-white w-4' : 'bg-white/40'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
import { useSubscription } from '../../context/SubscriptionContext';
import { analyzePhoto } from '../../services/aiInsights';

function Section({ title, emoji, children }) {
  return (
    <div className="mb-6">
      <h3 className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
        {emoji} {title}
      </h3>
      {children}
    </div>
  );
}

function PhotoAnalysisPanel({ entry }) {
  const { can } = useSubscription();
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = useCallback(async (force = false) => {
    if (force) {
      // clear cache for this entry before re-running
      localStorage.removeItem(`glowlytix_ai_photo_${entry.id}`);
    }
    setStatus('loading');
    setError('');
    try {
      const photoUrl = entry.photos?.find(p => p.useInTimelapse)?.url || entry.photos?.[0]?.url || entry.imageUrl;
      const data = await analyzePhoto(photoUrl, { id: entry.id, glowScore: entry.glowScore, tags: entry.tags });
      setResult(data);
      setStatus('success');
    } catch (e) {
      setError(e.message);
      setStatus('error');
    }
  }, [entry]);

  if (!can('advancedInsights')) return null;
  const primaryPhoto = entry.photos?.find(p => p.useInTimelapse)?.url || entry.photos?.[0]?.url || entry.imageUrl;
  if (!primaryPhoto) return null;

  const { analysis } = result || {};

  return (
    <Section title="AI Photo Analysis" emoji="✨">
      <AnimatePresence mode="wait">
        {status === 'idle' && (
          <motion.button
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => run(false)}
            className="w-full glass-card rounded-[2rem] p-5 flex items-center gap-4 active:opacity-80"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#7c3aed20' }}>
              <SafeIcon icon={FiZap} style={{ color: '#7c3aed' }} className="text-lg" />
            </div>
            <div className="text-left">
              <p className="font-sans text-sm font-semibold text-slate-800">Analyze this photo</p>
              <p className="font-sans text-xs text-slate-400 mt-0.5">AI skin assessment</p>
            </div>
          </motion.button>
        )}

        {status === 'loading' && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="glass-card rounded-[2rem] p-5 flex items-center gap-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              className="w-10 h-10 rounded-full border-2 border-violet-200 border-t-violet-600 flex-shrink-0"
            />
            <p className="font-sans text-sm text-slate-500">Analyzing skin…</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="glass-card rounded-[2rem] p-5">
            <div className="flex items-center gap-3 mb-3">
              <SafeIcon icon={FiAlertCircle} className="text-rose-400 text-lg flex-shrink-0" />
              <p className="font-sans text-sm text-slate-600">{error}</p>
            </div>
            <button onClick={() => run(false)}
              className="font-sans text-xs text-slate-500 underline underline-offset-2">
              Try again
            </button>
          </motion.div>
        )}

        {status === 'success' && analysis && (
          <motion.div key="result" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-[2rem] overflow-hidden">
            {/* Overall + rating */}
            <div className="p-5 pb-4">
              <div className="flex items-start justify-between mb-3">
                <p className="font-sans text-sm text-slate-700 leading-relaxed flex-1 pr-4">{analysis.overall}</p>
                <div className="flex flex-col items-center flex-shrink-0">
                  <span className="font-serif text-2xl text-slate-900">{analysis.glowRating}</span>
                  <span className="font-sans text-xs text-slate-400">/10</span>
                </div>
              </div>

              {/* Attribute pills */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { label: analysis.texture, color: '#5C8A78' },
                  { label: analysis.hydration, color: '#60a5fa' },
                  { label: analysis.tone, color: '#f59e0b' },
                ].filter(a => a.label).map(a => (
                  <span key={a.label}
                    className="font-sans text-xs font-medium px-3 py-1 rounded-full capitalize"
                    style={{ background: `${a.color}18`, color: a.color }}>
                    {a.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Positives */}
            {analysis.positives?.length > 0 && (
              <div className="px-5 py-3 border-t border-white/50">
                <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Looking Good</p>
                {analysis.positives.map((p, i) => (
                  <p key={i} className="font-sans text-xs text-slate-600 leading-relaxed">✓ {p}</p>
                ))}
              </div>
            )}

            {/* Watch points */}
            {analysis.watchPoints?.length > 0 && (
              <div className="px-5 py-3 border-t border-white/50">
                <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">To Watch</p>
                {analysis.watchPoints.map((w, i) => (
                  <p key={i} className="font-sans text-xs text-slate-600 leading-relaxed">• {w}</p>
                ))}
              </div>
            )}

            {/* Tip */}
            {analysis.tip && (
              <div className="px-5 py-4 border-t border-white/50 rounded-b-[2rem]"
                style={{ background: '#7c3aed08' }}>
                <p className="font-sans text-xs font-semibold text-violet-600 mb-1">AI Tip</p>
                <p className="font-sans text-xs text-violet-700 leading-relaxed">{analysis.tip}</p>
              </div>
            )}

            {/* Refresh */}
            <div className="px-5 pb-4 pt-1 flex justify-end">
              <button onClick={() => run(true)}
                className="flex items-center gap-1.5 font-sans text-xs text-slate-400 active:text-slate-600">
                <SafeIcon icon={FiRefreshCw} className="text-xs" />
                Re-analyze
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
}

export default function EntryDetail({ entry, onClose }) {
  if (!entry) return null;

  const amProducts = entry.routines?.AM || [];
  const pmProducts = entry.routines?.PM || [];

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      className="absolute inset-0 z-50 overflow-y-auto bg-[#f8fafc]"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-20%] w-[70%] h-[50%] rounded-full bg-[#d1ece5] blur-3xl opacity-60 flex-shrink-0" />
        <div className="absolute bottom-[-10%] right-[-20%] w-[80%] h-[50%] rounded-full bg-[#e6e0f8] blur-3xl opacity-60 flex-shrink-0" />
      </div>

      <div className="relative z-10">
        <PhotoGallery entry={entry} onClose={onClose} />

        <div className="p-6 pb-20">
          {entry.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {entry.tags.map(t => <TagPill key={t} tag={t} />)}
            </div>
          )}

          {/* AI Photo Analysis — Radiant Pro only */}
          <PhotoAnalysisPanel entry={entry} />

          <Section title="Hydration" emoji="💧">
            <div className={`flex items-center gap-4 glass-card rounded-[2rem] px-5 py-4 ${entry.water >= 64 ? 'border-[#bbf7d0]' : ''}`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${entry.water >= 64 ? 'bg-[#d1ece5] text-[#0f766e]' : 'bg-white/60 text-slate-400'}`}>
                <SafeIcon icon={FiDroplet} className="text-xl" />
              </div>
              <span className="font-sans text-slate-800 font-medium text-lg">{entry.water} oz</span>
              {entry.water >= 64 && <span className="ml-auto text-xs text-[#0f766e] font-sans font-medium bg-[#d1ece5] px-3 py-1 rounded-full">Goal met ✓</span>}
            </div>
          </Section>

          <Section title="Skincare Routines" emoji="🧴">
            <div className="space-y-4">
              {['AM', 'PM'].map(time => {
                const products = time === 'AM' ? amProducts : pmProducts;
                return (
                  <div key={time} className="glass-card rounded-[2rem] p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center">
                        <SafeIcon icon={time === 'AM' ? FiSun : FiMoon} className="text-slate-800 text-lg" />
                      </div>
                      <span className="font-sans text-base font-medium text-slate-800">{time} Routine</span>
                    </div>
                    {products.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {products.map((p, i) => (
                          <span key={i} className="text-sm glass-input text-slate-700 px-3.5 py-1.5 rounded-full font-sans">
                            {p}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 font-sans ml-1">Not completed</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {entry.treatments?.length > 0 && (
            <Section title="Treatments" emoji="💉">
              {entry.treatments.map((t, i) => (
                <div key={i} className="glass-card rounded-[2rem] p-5 mb-3 border-[#fbd7cd]">
                  <p className="font-sans font-medium text-slate-800 text-base">{t.type}</p>
                  {t.area && <p className="text-sm text-slate-500 font-sans mt-1">Area: {t.area}</p>}
                  {t.notes && <p className="text-sm text-slate-500 font-sans mt-2">"{t.notes}"</p>}
                </div>
              ))}
            </Section>
          )}

          {entry.supplements?.length > 0 && (
            <Section title="Supplements" emoji="💊">
              <div className="flex flex-wrap gap-3">
                {entry.supplements.map((s, i) => (
                  <div key={i} className="glass-card rounded-2xl px-4 py-3 border-[#e5e1f9]">
                    <p className="text-sm font-sans font-medium text-slate-800">{s.name}</p>
                    {s.dose && <p className="text-xs font-sans text-slate-500 mt-0.5">{s.dose}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {entry.makeup?.wore && (
            <Section title="Makeup" emoji="💄">
              <div className="glass-card rounded-[2rem] p-5">
                {entry.makeup.products?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {entry.makeup.products.map((p, i) => (
                      <span key={i} className="text-sm glass-input text-slate-700 px-3.5 py-1.5 rounded-full font-sans capitalize">{p}</span>
                    ))}
                  </div>
                )}
                <p className="font-sans text-xs text-slate-500">
                  {entry.makeup.doubleCleansed ? '✓ Double cleansed' : '⚠ Did not double cleanse'}
                </p>
              </div>
            </Section>
          )}

          {entry.cycle?.periodDay && (
            <Section title="Cycle" emoji="🩸">
              <div className="glass-card rounded-[2rem] p-5">
                {entry.cycle.flow && (
                  <p className="font-sans text-sm text-slate-700 mb-2 capitalize">Flow: {entry.cycle.flow}</p>
                )}
                {entry.cycle.symptoms?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {entry.cycle.symptoms.map((s, i) => (
                      <span key={i} className="font-sans text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-500 border border-rose-100">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            </Section>
          )}

          {entry.notes && (
            <Section title="Notes" emoji="📝">
              <div className="glass-card rounded-[2rem] p-5">
                <p className="font-sans text-base text-slate-700 leading-relaxed">"{entry.notes}"</p>
              </div>
            </Section>
          )}
        </div>
      </div>
    </motion.div>
  );
}
